/**
 * The local record for one session — chiefly so routing can see
 * `post_attempted_at`.
 *
 * Separate from `useDraftSync` because it is needed *before* any screen renders:
 * § 4.5's rule 4 says reopening a session with the marker set and no terminal
 * outcome goes **straight to verifying**, which is a routing decision, and routing
 * happens above the screens.
 */
import { useCallback, useEffect, useState } from "react";

import type { SessionRecord } from "./db";
import { onSessionRecordChange, readSessionRecord } from "./draftStore";

export interface SessionRecordState {
  record: SessionRecord | undefined;
  /** False until the store has actually been asked. */
  loaded: boolean;
  reload: () => Promise<void>;
}

export function useSessionRecord(sessionId: string): SessionRecordState {
  const [record, setRecord] = useState<SessionRecord | undefined>(undefined);
  const [loaded, setLoaded] = useState(false);

  const reload = useCallback(async () => {
    try {
      setRecord(await readSessionRecord(sessionId));
    } catch {
      // IndexedDB unavailable (private mode, or a browser refusing storage).
      // Treated as "no marker": the draft store is a resilience layer and must
      // never be the thing that stops a manager reaching a screen. The post's own
      // safety does not depend on this record — the server is the authority on
      // whether a session posted, and `POST` is refused outside `InReview`.
      setRecord(undefined);
    } finally {
      setLoaded(true);
    }
  }, [sessionId]);

  useEffect(() => {
    setLoaded(false);
    void reload();
  }, [reload]);

  // Re-read when the post marker moves. Without this the hook answers with whatever
  // the store held when the screen it is under first mounted — and the marker is
  // written *between* the confirm tap and the screen that must honour it, with no
  // remount in between. See `onSessionRecordChange`.
  useEffect(
    () =>
      onSessionRecordChange((changed) => {
        if (changed === sessionId) void reload();
      }),
    [reload, sessionId],
  );

  return { record, loaded, reload };
}
